import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../store'

import { getTeams, postTeam, putTeam, deleteTeam } from '../lib/api'

type TeamsState = {
  teams: APITeam[]
  loading: boolean
  loaded: boolean
  error: any
}

const initialState: TeamsState = {
  teams: [],
  loading: false,
  loaded: false,
  error: null
}

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    startLoad: (state: TeamsState) => {
      state.loading = true
      state.error = null
    },

    setTeams: (state: TeamsState, action: PayloadAction<APITeam[]>) => {
      state.teams = action.payload
      state.loaded = true
      state.loading = false
    },

    addTeam: (state: TeamsState, action: PayloadAction<APITeam>) => {
      state.teams.push(action.payload)
    },

    update: (state: TeamsState, action: PayloadAction<APITeam>) => {
      const id = action.payload.id
      const idx = state.teams.findIndex(box => box.id === id)
      if (idx >= 0) {
        state.teams[idx] = action.payload
      }
    },

    remove: (state: TeamsState, action: PayloadAction<string>) => {
      const id = action.payload
      state.teams = state.teams.filter(i => i.id !== id)
    },

    setError: (state: TeamsState, action: PayloadAction<any>) => {
      state.error = action.payload
    }
  }
})

const { startLoad, setTeams, addTeam, update, remove, setError } =
  teamsSlice.actions

export const fetchTeams = () => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { loaded, loading } = selectTeams(getState())

      if (loaded || loading) return
      dispatch(startLoad())
      const teams = await getTeams()
      dispatch(setTeams(teams))
    } catch (err) {
      console.error('Error fetching teams', err)
      dispatch(setError({ err, auto: true }))
    }
  }
}

export const createTeam = (data: TeamUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      const team = await postTeam(data)
      dispatch(addTeam(team))
    } catch (err) {
      console.error('Error creating team', err)
      dispatch(setError({ err, auto: true }))
      throw err
    }
  }
}

export const updateTeam = (id: string, data: TeamUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      const team = await putTeam(id, data)
      dispatch(update(team))
    } catch (err) {
      console.error('Error updating teams', err)
      dispatch(setError({ err, auto: true }))
      //allow error to go next catch
      throw err
    }
  }
}

export const removeTeam = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await deleteTeam(id)
      dispatch(remove(id))
    } catch (err) {
      console.error('Error deleting team', err)
      dispatch(setError({ err, auto: true }))
      //allow error to go next catch
      throw err
    }
  }
}

export const removeMemberFromTeams = (id: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { loaded, teams } = selectTeams(getState())

      let teamsData = [] as APITeam[]
      if (loaded) {
        teamsData = [...teams]
      } else {
        teamsData = await getTeams()
      }
      for (let i = 0; i < teamsData.length; i++) {
        const team = teamsData[i]
        let newMembers = [] as string[]
        let newLeaders = [] as string[]

        const isMember = team.members.some(m => m === id)
        const isLeader = team.leaders.some(l => l === id)
        if (isMember) {
          newMembers = team.members.filter(m => m !== id)
        }
        if (isLeader) {
          newLeaders = team.leaders.filter(l => l !== id)
        }
        if (isMember || isLeader) {
          const { id, created_at, updated_at, ...rest } = team
          const updatedTeam = {
            ...rest,
            members: newMembers,
            leaders: newLeaders
          }
          await putTeam(team.id, updatedTeam)
          teamsData[i] = { ...updatedTeam, id, created_at, updated_at }
        }
      }
      dispatch(setTeams(teamsData))
    } catch (err) {
      console.error('Error fetching teams', err)
      dispatch(setError({ err, auto: true }))
    }
  }
}

export const selectTeams = (state: RootState) => state.teams

export default teamsSlice.reducer
